if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // 🆕 Observe scroll position
        window.addEventListener('scroll', debounce(this.onScrollChange.bind(this), 100));
      }

      onSlideChanged(event) {
        const detail = event?.detail || {};
        let currentElement = detail.currentElement;

        if (!currentElement && typeof detail.index !== 'undefined') {
          const slides = this.elements.viewer?.querySelectorAll('[data-media-id]') || [];
          currentElement = slides[detail.index] || null;
        }

        if (!currentElement) {
          currentElement = this.elements.viewer?.querySelector('.is-active,[aria-current="true"]');
        }

        if (!currentElement) {
          console.warn('Slide changed event missing currentElement:', event);
          return;
        }

        const mediaId = currentElement.dataset?.mediaId;
        if (!mediaId) {
          console.warn('Missing mediaId in currentElement:', currentElement);
          return;
        }

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!thumbnail) {
          console.warn('No thumbnail found for mediaId:', mediaId);
          return;
        }

        this.setActiveThumbnail(thumbnail);
      }

      // 🆕 Detect which media is visible on scroll
      onScrollChange() {
        const medias = [...this.elements.viewer.querySelectorAll('[data-media-id]')];
        const headerOffset = (this.stickyHeader?.offsetHeight || 0) + 10;
        const scrollMid = window.scrollY + headerOffset + window.innerHeight / 2;

        let activeMedia = null;

        for (const media of medias) {
          const rect = media.getBoundingClientRect();
          const mediaTop = rect.top + window.scrollY;
          const mediaBottom = rect.bottom + window.scrollY;

          // Check if scroll midpoint is within this media section
          if (scrollMid >= mediaTop && scrollMid <= mediaBottom) {
            activeMedia = media;
            break;
          }
        }

        if (!activeMedia) return;

        const mediaId = activeMedia.dataset.mediaId;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);

        if (activeThumbnail && !activeThumbnail.querySelector('button[aria-current="true"]')) {
          this.setActiveThumbnail(activeThumbnail);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        // Remove active class from all and add to current
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
        activeMedia.classList.add('is-active');

        // Optional: Prepend logic
        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail.parentElement.firstChild !== activeThumbnail)
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        // Scroll active media into view (horizontal + vertical)
        window.setTimeout(() => {
          // Horizontal scroll
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }

          // Vertical scroll (with 10px offset from header)
          const rect = activeMedia.getBoundingClientRect();
          const headerOffset = (this.stickyHeader?.offsetHeight || 0) + 10;
          const mediaTop = rect.top + window.scrollY - headerOffset;
          const mediaBottom = rect.bottom + window.scrollY - window.innerHeight + headerOffset;

          if (rect.top < headerOffset) {
            window.scrollTo({ top: mediaTop, behavior: 'smooth' });
          } else if (rect.bottom > window.innerHeight) {
            // window.scrollTo({ top: mediaBottom, behavior: 'smooth' });
            window.scrollTo({ top: mediaTop, behavior: 'smooth' });
          }
        }, 50);

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((el) => el.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        // Smooth horizontal scroll to make thumbnail visible
        const slider = this.elements.thumbnails;
        const offset = thumbnail.offsetLeft - slider.offsetLeft;
        slider.scrollTo({ left: offset, behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
